import { tuiRound } from '@taiga-ui/cdk/utils/math';
import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { inject, signal, computed, Component, ChangeDetectionStrategy, Input, INJECTOR, Directive, forwardRef, ContentChild } from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl, NgModel } from '@angular/forms';
import { TuiControl } from '@taiga-ui/cdk/classes';
import { tuiWatch, tuiControlValue, tuiTypedFromEvent } from '@taiga-ui/cdk/observables';
import { tuiFallbackValueProvider } from '@taiga-ui/cdk/tokens';
import { tuiCreateToken, tuiProvideOptions, tuiPure } from '@taiga-ui/cdk/utils/miscellaneous';
import { take, timer, switchMap, merge, tap, map, combineLatest, filter } from 'rxjs';
import { tuiInjectElement } from '@taiga-ui/cdk/utils/dom';
import { tuiAsAuxiliary } from '@taiga-ui/core/components/textfield';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOCUMENT, AsyncPipe, NgIf } from '@angular/common';
import { TUI_TRUE_HANDLER, TUI_FALSE_HANDLER } from '@taiga-ui/cdk/constants';

/**
 * Used as a limit for eliminating JS issues with floating point math
 */
const _c0 = ["type", "range", "tuiSlider", ""];
const _c1 = ["tuiSliderThumbLabel", ""];
const _c2 = ["*", [["input", "type", "range"]]];
const _c3 = ["*", "input[type=range]"];
function TuiSliderThumbLabel_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
const TUI_FLOATING_PRECISION = 7;
function tuiFindKeyStepsBoundariesByFn(keySteps, fn) {
  const keyStepUpperIndex = keySteps.findIndex((keyStep, i) => i && fn(keyStep));
  const lowerStep = keySteps[keyStepUpperIndex - 1] || keySteps[0];
  const upperStep = keySteps[keyStepUpperIndex] || keySteps[keySteps.length - 1] || [0, 0];
  return [lowerStep, upperStep];
}
function tuiPercentageToKeyStepValue(valuePercentage, keySteps) {
  const [[lowerStepPercent, lowerStepValue], [upperStepPercent, upperStepValue]] = tuiFindKeyStepsBoundariesByFn(keySteps, ([keyStepPercentage, _]) => valuePercentage <= keyStepPercentage);
  const ratio = (valuePercentage - lowerStepPercent) / (upperStepPercent - lowerStepPercent);
  const controlValue = (upperStepValue - lowerStepValue) * ratio + lowerStepValue;
  return tuiRound(controlValue, TUI_FLOATING_PRECISION);
}
function tuiKeyStepValueToPercentage(value, keySteps) {
  const [[lowerStepPercent, lowerStepValue], [upperStepPercent, upperStepValue]] = tuiFindKeyStepsBoundariesByFn(keySteps, ([_, keyStepValue]) => value <= keyStepValue);
  const ratio = (value - lowerStepValue) / (upperStepValue - lowerStepValue) || 0;
  return (upperStepPercent - lowerStepPercent) * ratio + lowerStepPercent;
}
function tuiCreateKeyStepsTransformer(keySteps, slider) {
  return new class {
    fromControlValue(controlValue) {
      const newValuePercentage = tuiKeyStepValueToPercentage(controlValue, keySteps);
      return newValuePercentage * (slider.max - slider.min) / 100 + slider.min;
    }
    toControlValue(nativeValue) {
      const valueRatio = (nativeValue - slider.min) / (slider.max - slider.min) || 0;
      return tuiPercentageToKeyStepValue(valueRatio * 100, keySteps);
    }
  }();
}
const TUI_SLIDER_DEFAULT_OPTIONS = {
  size: 'm',
  trackColor: 'var(--tui-background-neutral-2)'
};
/**
 * Default parameters for Slider component
 */
const TUI_SLIDER_OPTIONS = tuiCreateToken(TUI_SLIDER_DEFAULT_OPTIONS);
function tuiSliderOptionsProvider(options) {
  return tuiProvideOptions(TUI_SLIDER_OPTIONS, options, TUI_SLIDER_DEFAULT_OPTIONS);
}
let TuiSliderComponent = /*#__PURE__*/(() => {
  class TuiSliderComponent {
    constructor() {
      this.control = inject(NgControl, {
        self: true,
        optional: true
      });
      this.options = inject(TUI_SLIDER_OPTIONS);
      this.segments = signal([1]);
      this.ticksGradient = computed((segments = this.segments()) => this.getTicksGradient(segments));
      this.size = this.options.size;
      this.el = tuiInjectElement();
      this.keySteps = inject(TuiSliderKeyStepsBase, {
        self: true,
        optional: true
      });
      if (this.control instanceof NgModel) {
        /**
         * The ValueAccessor.writeValue method is called twice on any value accessor during component initialization,
         * when a control is bound using [(ngModel)], first time with a phantom null value.
         * With `changeDetection: ChangeDetectionStrategy.OnPush` the second call of writeValue with real value don't re-render the view.
         * ___
         * See this {@link https://github.com/angular/angular/issues/14988 issue}
         */
        this.control.valueChanges?.pipe(tuiWatch(), take(1)).subscribe();
      }
    }
    // TODO(v5): use signal inputs
    set segmentsSetter(segments) {
      this.segments.set(segments);
    }
    get valueRatio() {
      return (this.value - this.min) / (this.max - this.min) || 0;
    }
    get min() {
      return Number(this.el.min);
    }
    set min(x) {
      this.el.min = String(x);
    }
    get max() {
      return Number(this.el.max || 100);
    }
    set max(x) {
      this.el.max = String(x);
    }
    get step() {
      if (!this.el.step) {
        return 1;
      }
      return this.el.step === 'any' ? 0 : Number(this.el.step);
    }
    set step(x) {
      this.el.step = String(x);
    }
    get value() {
      /**
       * If developer uses `[(ngModel)]` and programmatically change value,
       * the `el.nativeElement.value` is equal to the previous value at this moment
       * (it will be updated only in next microtask).
       * @see https://github.com/angular/angular/issues/13568
       */
      if (this.control instanceof NgModel) {
        const transformer = this.keySteps?.transformer();
        const value = transformer ? transformer.fromControlValue(this.control.value) : this.control.viewModel;
        return this.step ? tuiRound(Math.round(value / this.step) * this.step, TUI_FLOATING_PRECISION) : value;
      }
      return Number(this.el.value) || 0;
    }
    set value(newValue) {
      this.el.value = `${newValue}`;
    }
    getTicksGradient(segments) {
      if (segments.length <= 1) {
        return 'linear-gradient(to right, transparent 0 100%)';
      }
      const percentages = segments.filter(segment => segment > 0 && segment < 1).map(segment => segment * 100);
      return percentages.reduce((acc, segment, index) => `${acc}
                var(--tui-text-tertiary) ${segment}% calc(${segment}% + var(--t-tick-thickness)),
                transparent ${segment}% ${percentages[index + 1] ?? 100}%${percentages[index + 1] ? ',' : ')'}
                `, `linear-gradient(to right, transparent 0 ${percentages[0]}%,`);
    }
    static {
      this.ɵfac = function TuiSliderComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiSliderComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiSliderComponent,
        selectors: [["input", "type", "range", "tuiSlider", ""]],
        hostVars: 7,
        hostBindings: function TuiSliderComponent_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("input", function TuiSliderComponent_input_HostBindingHandler() {
              return 0;
            });
          }
          if (rf & 2) {
            i0.ɵɵattribute("data-size", ctx.size);
            i0.ɵɵstyleProp("--tui-slider-track-color", ctx.options.trackColor)("--tui-ticks-gradient", ctx.ticksGradient())("--tui-slider-fill-ratio", ctx.valueRatio);
          }
        },
        inputs: {
          size: "size",
          segmentsSetter: [2, "segments", "segmentsSetter", x => Array.isArray(x) ? x : new Array(x).fill(null).map((_, i) => i / x)]
        },
        features: [i0.ɵɵProvidersFeature([tuiAsAuxiliary(TuiSliderComponent)])],
        attrs: _c0,
        decls: 0,
        vars: 0,
        template: function TuiSliderComponent_Template(rf, ctx) {},
        styles: ["[_nghost-%COMP%]{--t-tick-thickness: .25rem;position:relative;display:block;inline-size:100%;color:var(--tui-background-accent-1);cursor:pointer;-webkit-appearance:none;appearance:none;block-size:.125rem;padding:.4375rem 0;background-color:transparent;background-clip:content-box;outline:none;border-radius:var(--tui-radius-m)}[_nghost-%COMP%]:active{cursor:ew-resize}[_nghost-%COMP%]:disabled{opacity:var(--tui-disabled-opacity);cursor:auto}[data-size=s][_nghost-%COMP%]:not(:disabled):before{transition-property:transform,opacity;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;position:absolute;top:.25rem;left:calc(var(--tui-slider-fill-ratio) * 100% - var(--tui-slider-fill-ratio) * .5rem);inline-size:.5rem;block-size:.5rem;border-radius:50%;transform:var(--tui-slider-thumb-transform, scale(1));content:\"\";cursor:ew-resize;background:currentColor;opacity:0}[data-size=s][_nghost-%COMP%]:active:before{opacity:.2;transform:var(--tui-slider-thumb-transform, scale(1)) scale(2.33)}[data-size=m][_nghost-%COMP%]:not(:disabled):before{transition-property:transform,opacity;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;position:absolute;top:.125rem;left:calc(var(--tui-slider-fill-ratio) * 100% - var(--tui-slider-fill-ratio) * .75rem);inline-size:.75rem;block-size:.75rem;border-radius:50%;transform:var(--tui-slider-thumb-transform, scale(1));content:\"\";cursor:ew-resize;background:currentColor;opacity:0}[data-size=m][_nghost-%COMP%]:active:before{opacity:.2;transform:var(--tui-slider-thumb-transform, scale(1)) scale(2.33)}[_nghost-%COMP%]::-webkit-slider-container{border-radius:inherit}[data-size=m][_nghost-%COMP%]::-webkit-slider-runnable-track{block-size:.125rem;border-radius:inherit;background-repeat:no-repeat;background-color:var(--tui-slider-track-color);background-image:var(--tui-ticks-gradient),linear-gradient(to right,currentColor calc(100% * var(--tui-slider-fill-ratio)),transparent calc(100% * var(--tui-slider-fill-ratio)));background-position-x:calc((.75rem - var(--t-tick-thickness)) / 2),0;background-size:calc(100% - .75rem),auto}[data-size=s][_nghost-%COMP%]::-webkit-slider-runnable-track{block-size:.125rem;border-radius:inherit;background-repeat:no-repeat;background-color:var(--tui-slider-track-color);background-image:var(--tui-ticks-gradient),linear-gradient(to right,currentColor calc(100% * var(--tui-slider-fill-ratio)),transparent calc(100% * var(--tui-slider-fill-ratio)));background-position-x:calc((.5rem - var(--t-tick-thickness)) / 2),0;background-size:calc(100% - .5rem),auto}[data-size=m][_nghost-%COMP%]::-moz-range-track{block-size:.125rem;border-radius:inherit;background-repeat:no-repeat;background-color:var(--tui-slider-track-color);background-image:var(--tui-ticks-gradient);background-position-x:calc((.75rem - var(--t-tick-thickness)) / 2);background-size:calc(100% - .75rem)}[data-size=s][_nghost-%COMP%]::-moz-range-track{block-size:.125rem;border-radius:inherit;background-repeat:no-repeat;background-color:var(--tui-slider-track-color);background-image:var(--tui-ticks-gradient);background-position-x:calc((.5rem - var(--t-tick-thickness)) / 2);background-size:calc(100% - .5rem)}[data-size=m][_nghost-%COMP%]::-webkit-slider-thumb{-webkit-transition-property:transform;transition-property:transform;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;isolation:isolate;-webkit-appearance:none;appearance:none;background-color:currentColor;box-shadow:none;border-radius:50%;block-size:.75rem;inline-size:.75rem;box-sizing:content-box;background-clip:content-box;border:.125rem solid transparent;border-inline-start:0;border-inline-end:0;transform:var(--tui-slider-thumb-transform, scale(1));margin-top:-.4375rem}:not(:disabled)[data-size=m][_nghost-%COMP%]::-webkit-slider-thumb{cursor:ew-resize}:not(:disabled)[data-size=m][_nghost-%COMP%]::-webkit-slider-thumb:hover, :active:not(:disabled)[data-size=m][_nghost-%COMP%]::-webkit-slider-thumb{transform:var(--tui-slider-thumb-transform, scale(1)) scale(1.333)}:focus-visible[data-size=m][_nghost-%COMP%]::-webkit-slider-thumb{box-shadow:0 0 0 2px inset var(--tui-border-focus)}[data-size=s][_nghost-%COMP%]::-webkit-slider-thumb{-webkit-transition-property:transform;transition-property:transform;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;isolation:isolate;-webkit-appearance:none;appearance:none;background-color:currentColor;box-shadow:none;border-radius:50%;block-size:.5rem;inline-size:.5rem;box-sizing:content-box;background-clip:content-box;border:.25rem solid transparent;border-inline-start:0;border-inline-end:0;transform:var(--tui-slider-thumb-transform, scale(1));margin-top:-.4375rem}:not(:disabled)[data-size=s][_nghost-%COMP%]::-webkit-slider-thumb{cursor:ew-resize}:not(:disabled)[data-size=s][_nghost-%COMP%]::-webkit-slider-thumb:hover, :active:not(:disabled)[data-size=s][_nghost-%COMP%]::-webkit-slider-thumb{transform:var(--tui-slider-thumb-transform, scale(1)) scale(1.5)}:focus-visible[data-size=s][_nghost-%COMP%]::-webkit-slider-thumb{box-shadow:0 0 0 2px inset var(--tui-border-focus)}[data-size=m][_nghost-%COMP%]::-moz-range-thumb{-moz-transition-property:transform;transition-property:transform;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;isolation:isolate;-webkit-appearance:none;appearance:none;background-color:currentColor;box-shadow:none;border-radius:50%;block-size:.75rem;inline-size:.75rem;box-sizing:content-box;background-clip:content-box;border:.125rem solid transparent;border-inline-start:0;border-inline-end:0;transform:var(--tui-slider-thumb-transform, scale(1))}:not(:disabled)[data-size=m][_nghost-%COMP%]::-moz-range-thumb{cursor:ew-resize}:not(:disabled)[data-size=m][_nghost-%COMP%]::-moz-range-thumb:hover, :active:not(:disabled)[data-size=m][_nghost-%COMP%]::-moz-range-thumb{transform:var(--tui-slider-thumb-transform, scale(1)) scale(1.333)}:focus-visible[data-size=m][_nghost-%COMP%]::-moz-range-thumb{box-shadow:0 0 0 2px inset var(--tui-border-focus)}[data-size=s][_nghost-%COMP%]::-moz-range-thumb{-moz-transition-property:transform;transition-property:transform;transition-duration:var(--tui-duration, .3s);transition-timing-function:ease-in-out;isolation:isolate;-webkit-appearance:none;appearance:none;background-color:currentColor;box-shadow:none;border-radius:50%;block-size:.5rem;inline-size:.5rem;box-sizing:content-box;background-clip:content-box;border:.25rem solid transparent;border-inline-start:0;border-inline-end:0;transform:var(--tui-slider-thumb-transform, scale(1))}:not(:disabled)[data-size=s][_nghost-%COMP%]::-moz-range-thumb{cursor:ew-resize}:not(:disabled)[data-size=s][_nghost-%COMP%]::-moz-range-thumb:hover, :active:not(:disabled)[data-size=s][_nghost-%COMP%]::-moz-range-thumb{transform:var(--tui-slider-thumb-transform, scale(1)) scale(1.5)}:focus-visible[data-size=s][_nghost-%COMP%]::-moz-range-thumb{box-shadow:0 0 0 2px inset var(--tui-border-focus)}[_nghost-%COMP%]::-moz-range-progress{border-radius:inherit}[_nghost-%COMP%]::-moz-range-progress{block-size:.125rem;background:currentColor;border-top-right-radius:0;border-bottom-right-radius:0}tui-textfield   [type=\"range\"][_nghost-%COMP%]{--tui-radius: var(--tui-radius-m);position:absolute;top:100%;left:calc(var(--tui-radius) / 2);right:0;inline-size:calc(100% - calc(var(--tui-radius) / 2));box-sizing:border-box;block-size:1rem;margin:-.5625rem 0 0;padding:0;border-top-left-radius:0;border-bottom-left-radius:calc(var(--tui-radius) * 10) calc(var(--tui-radius) * 2);pointer-events:auto}tui-textfield[data-size=\"l\"]   [type=\"range\"][_nghost-%COMP%]{--tui-radius: var(--tui-radius-l)}"],
        changeDetection: 0
      });
    }
  }
  return TuiSliderComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class TuiSliderKeyStepsBase {
  constructor() {
    this.injector = inject(INJECTOR);
    this.control = inject(NgControl, {
      self: true,
      optional: true
    });
    this.step = 1;
    this.transformer = signal(null);
    this.value = toSignal(timer(0) // https://github.com/angular/angular/issues/54418
    .pipe(switchMap(() => tuiControlValue(this.control))));
  }
  get slider() {
    return this.injector.get(TuiSliderComponent);
  }
  set keySteps(steps) {
    this.transformer.set(steps && tuiCreateKeyStepsTransformer(steps, this.slider));
    this.min = steps?.[0][1];
    this.max = steps?.[steps.length - 1]?.[1];
  }
  /**
   * TODO(v5): standardize logic between `TuiSlider` & `TuiInputSlider` (for non-linear slider `step` means percentage)
   * Add these host-bindings to `TuiSliderKeyStepsBase`:
   * ```
   * host: {
   *     '[attr.min]': '0',
   *     '[attr.step]': '1',
   *     '[attr.max]': 'totalSteps',
   * },
   * ```
   */
  get totalSteps() {
    /**
     * Not-integer amount of steps is invalid usage of native sliders
     * ```html
     * <input type="range" [max]="100" [step]="3.33" />
     * ```
     * (impossible to select 100; 99.9 is max allowed value)
     */
    return this.step ? Math.round(100 / this.step) : Infinity;
  }
  takeStep(coefficient) {
    const newValue = this.slider.value + coefficient;
    return this.transformer()?.toControlValue(this.slider.value + coefficient) ?? newValue;
  }
  static {
    this.ɵfac = function TuiSliderKeyStepsBase_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || TuiSliderKeyStepsBase)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: TuiSliderKeyStepsBase,
      selectors: [["input", "tuiSlider", "", "keySteps", ""]],
      hostVars: 3,
      hostBindings: function TuiSliderKeyStepsBase_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("aria-valuemin", ctx.min)("aria-valuemax", ctx.max)("aria-valuenow", ctx.value());
        }
      },
      inputs: {
        step: [2, "step", "step", x => x === 'any' ? null : x],
        keySteps: "keySteps"
      }
    });
  }
}
__decorate([tuiPure], TuiSliderKeyStepsBase.prototype, "slider", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TuiSliderKeySteps = /*#__PURE__*/(() => {
  class TuiSliderKeySteps extends TuiControl {
    constructor() {
      super(...arguments);
      this.slider = inject(forwardRef(() => TuiSliderComponent));
    }
    set keySteps(steps) {
      this.transformer = tuiCreateKeyStepsTransformer(steps, this.slider);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵTuiSliderKeySteps_BaseFactory;
        return function TuiSliderKeySteps_Factory(__ngFactoryType__) {
          return (ɵTuiSliderKeySteps_BaseFactory || (ɵTuiSliderKeySteps_BaseFactory = i0.ɵɵgetInheritedFactory(TuiSliderKeySteps)))(__ngFactoryType__ || TuiSliderKeySteps);
        };
      })();
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiSliderKeySteps,
        selectors: [["input", "tuiSlider", "", "keySteps", "", "ngModel", ""], ["input", "tuiSlider", "", "keySteps", "", "formControl", ""], ["input", "tuiSlider", "", "keySteps", "", "formControlName", ""]],
        hostVars: 2,
        hostBindings: function TuiSliderKeySteps_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("blur", function TuiSliderKeySteps_blur_HostBindingHandler() {
              return ctx.onTouched();
            })("input", function TuiSliderKeySteps_input_HostBindingHandler($event) {
              return ctx.onChange($event.target.value);
            })("change", function TuiSliderKeySteps_change_HostBindingHandler($event) {
              return ctx.onChange($event.target.value);
            });
          }
          if (rf & 2) {
            i0.ɵɵdomProperty("value", ctx.value())("disabled", ctx.disabled());
          }
        },
        inputs: {
          keySteps: "keySteps"
        },
        features: [i0.ɵɵProvidersFeature([tuiFallbackValueProvider(0)]), i0.ɵɵInheritDefinitionFeature]
      });
    }
  }
  return TuiSliderKeySteps;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const SLIDER_INTERACTION_KEYS = new Set(['ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp']);
/**
 * Native <input type='range' readonly> doesn't work.
 * This directive imitates this native behaviour.
 */
let TuiSliderReadonly = /*#__PURE__*/(() => {
  class TuiSliderReadonly {
    constructor() {
      this.el = tuiInjectElement();
      this.doc = inject(DOCUMENT);
      this.readonly = true;
      const touchStart$ = tuiTypedFromEvent(this.el, 'touchstart', {
        passive: false
      });
      const touchMove$ = tuiTypedFromEvent(this.doc, 'touchmove', {
        passive: false
      });
      const touchEnd$ = tuiTypedFromEvent(this.doc, 'touchend', {
        passive: true
      });
      const shouldPreventMove$ = merge(touchStart$.pipe(tap(e => this.preventEvent(e)), map(TUI_TRUE_HANDLER)), touchEnd$.pipe(map(TUI_FALSE_HANDLER)));
      /**
       * @bad TODO think about another solution.
       * Keep in mind that preventing touch event (on slider) inside `@HostListener('touchstart')` doesn't work for mobile chrome.
       */
      combineLatest([touchMove$, shouldPreventMove$]).pipe(filter(([_, shouldPreventMove]) => shouldPreventMove), takeUntilDestroyed()).subscribe(([moveEvent]) => this.preventEvent(moveEvent));
    }
    preventEvent(event) {
      if (event.cancelable && this.readonly) {
        event.preventDefault();
      }
    }
    preventKeyboardInteraction(event) {
      if (SLIDER_INTERACTION_KEYS.has(event.key)) {
        this.preventEvent(event);
      }
    }
    static {
      this.ɵfac = function TuiSliderReadonly_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiSliderReadonly)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: TuiSliderReadonly,
        selectors: [["input", "tuiSlider", "", "readonly", ""]],
        hostBindings: function TuiSliderReadonly_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("keydown", function TuiSliderReadonly_keydown_HostBindingHandler($event) {
              return ctx.preventKeyboardInteraction($event);
            })("mousedown", function TuiSliderReadonly_mousedown_HostBindingHandler($event) {
              return ctx.preventEvent($event);
            });
          }
        },
        inputs: {
          readonly: [2, "readonly", "readonly", coerceBooleanProperty]
        }
      });
    }
  }
  return TuiSliderReadonly;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/// <reference types="@taiga-ui/tsconfig/ng-dev-mode" />
/// <reference types="@taiga-ui/tsconfig/ng-dev-mode" />
let TuiSliderThumbLabel = /*#__PURE__*/(() => {
  class TuiSliderThumbLabel {
    ngAfterContentInit() {
      ngDevMode && console.assert(Boolean(this.control?.valueChanges), '\n[tuiSliderThumbLabel] expected <input tuiSlider type="range" /> to use Angular Forms.\n' + 'Use [(ngModel)] or [formControl] or formControlName for correct work.');
    }
    get size() {
      return this.slider?.size || 'm';
    }
    get ratio() {
      return this.slider?.valueRatio || 0;
    }
    get ghostLeft() {
      return this.ratio * (this.slider?.el.offsetWidth || 0);
    }
    static {
      this.ɵfac = function TuiSliderThumbLabel_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiSliderThumbLabel)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiSliderThumbLabel,
        selectors: [["", "tuiSliderThumbLabel", ""]],
        contentQueries: function TuiSliderThumbLabel_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, TuiSliderComponent, 5);
            i0.ɵɵcontentQuery(dirIndex, NgControl, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.slider = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.control = _t.first);
          }
        },
        attrs: _c1,
        ngContentSelectors: _c3,
        decls: 5,
        vars: 8,
        consts: [[4, "ngIf"], [1, "t-ghost"]],
        template: function TuiSliderThumbLabel_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c2);
            i0.ɵɵtemplate(0, TuiSliderThumbLabel_ng_container_0_Template, 1, 0, "ng-container", 0);
            i0.ɵɵpipe(1, "async");
            i0.ɵɵelementStart(2, "div", 1);
            i0.ɵɵprojection(3);
            i0.ɵɵelementEnd();
            i0.ɵɵprojection(4, 1);
          }
          if (rf & 2) {
            i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 6, ctx.control == null ? null : ctx.control.valueChanges));
            i0.ɵɵadvance(2);
            i0.ɵɵstyleProp("--tui-slider-thumb-ratio", ctx.ratio)("left", ctx.ghostLeft, "px");
            i0.ɵɵattribute("data-size", ctx.size);
          }
        },
        dependencies: [AsyncPipe, NgIf],
        styles: ["[_nghost-%COMP%]{position:relative}.t-ghost[_ngcontent-%COMP%]{position:absolute;top:0;bottom:0;margin:auto;border-radius:50%;pointer-events:none}.t-ghost[data-size=s][_ngcontent-%COMP%]{inline-size:.5rem;block-size:.5rem;transform:translate(calc(var(--tui-slider-thumb-ratio) * -.5rem))}.t-ghost[data-size=m][_ngcontent-%COMP%]{inline-size:.75rem;block-size:.75rem;transform:translate(calc(var(--tui-slider-thumb-ratio) * -.75rem))}"],
        changeDetection: 0
      });
    }
  }
  return TuiSliderThumbLabel;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const TuiSlider = [TuiSliderComponent, TuiSliderThumbLabel, TuiSliderKeyStepsBase, TuiSliderKeySteps, TuiSliderReadonly];

/**
 * Generated bundle index. Do not edit.
 */

export { TUI_FLOATING_PRECISION, TUI_SLIDER_DEFAULT_OPTIONS, TUI_SLIDER_OPTIONS, TuiSlider, TuiSliderComponent, TuiSliderKeySteps, TuiSliderKeyStepsBase, TuiSliderReadonly, TuiSliderThumbLabel, tuiCreateKeyStepsTransformer, tuiKeyStepValueToPercentage, tuiPercentageToKeyStepValue, tuiSliderOptionsProvider };
